
import React, { useState } from 'react';
import { Job } from "@/components/jobs/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Edit3, Save, X, Plus } from 'lucide-react';
import { useUpdateJob } from '@/hooks/mutations/useUpdateJob';
import { toast } from 'sonner';

interface EditableJobSkillsProps {
  job: Job;
  canEdit: boolean;
}

export const EditableJobSkills: React.FC<EditableJobSkillsProps> = ({ job, canEdit }) => {
  const { mutate: updateJob, isPending: isUpdating } = useUpdateJob();
  const [isEditing, setIsEditing] = useState(false);
  const [editSkills, setEditSkills] = useState<string[]>(job.skills || []);
  const [newSkill, setNewSkill] = useState('');

  const handleSave = async () => {
    updateJob({ id: job.id, data: { skills: editSkills.length > 0 ? editSkills : null } }, {
      onSuccess: () => {
        toast.success('Skills updated successfully');
        setIsEditing(false);
      }
    });
  };

  const handleCancel = () => {
    setEditSkills(job.skills || []);
    setNewSkill('');
    setIsEditing(false);
  };

  const addSkill = () => {
    if (newSkill.trim() && !editSkills.includes(newSkill.trim())) {
      setEditSkills([...editSkills, newSkill.trim()]);
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setEditSkills(editSkills.filter(skill => skill !== skillToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  if (!job.skills?.length && !canEdit) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Required Skills</CardTitle>
          {canEdit && !isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="Add a skill..."
                onKeyPress={handleKeyPress}
              />
              <Button onClick={addSkill} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {editSkills.map((skill, index) => (
                <Badge key={index} variant="secondary" className="cursor-pointer">
                  {skill}
                  <X 
                    className="h-3 w-3 ml-1 hover:text-destructive" 
                    onClick={() => removeSkill(skill)}
                  />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={isUpdating}>
                <Save className="h-4 w-4 mr-2" />
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="outline" onClick={handleCancel} disabled={isUpdating}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {job.skills?.length ? (
              job.skills.map((skill, index) => (
                <Badge key={index} variant="secondary">
                  {skill}
                </Badge>
              ))
            ) : (
              canEdit && (
                <div className="text-muted-foreground italic">
                  No skills specified. Click Edit to add them.
                </div>
              )
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
